import { useState, useEffect } from "react";
import supabase from "../../utils/supabase";
import { toast } from "react-toastify";
import useUserStore from "../../stores/user";
import { useT } from "../../stores/uiLang";

// Run E2 J2: student notification dialog in English and Spanish (shared UI language store).
const STUDENT_SETTINGS_T = {
  en: {
    title: "Notification Settings",
    choose: "Choose which notifications you'd like to receive:",
    assignments: "Assignments",
    assignmentsHelp: "Get notified about new assignments and due dates",
    cancel: "Cancel",
    save: "Save Settings",
    saving: "Saving...",
    saveError: "Failed to save settings",
    saveOk: "Settings saved successfully",
    close: "Close",
  },
  es: {
    title: "Configuración de avisos",
    choose: "Elige qué avisos quieres recibir:",
    assignments: "Tareas",
    assignmentsHelp: "Recibe un aviso cuando haya tareas nuevas y fechas de entrega",
    cancel: "Cancelar",
    save: "Guardar configuración",
    saving: "Guardando...",
    saveError: "No se pudo guardar la configuración",
    saveOk: "Configuración guardada",
    close: "Cerrar",
  },
};

interface StudentSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const StudentSettingsModal: React.FC<StudentSettingsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const t = useT(STUDENT_SETTINGS_T);
  const user = useUserStore((state) => state.user);
  const profile = useUserStore((state) => state.profile);
  const { fetchUserProfile } = useUserStore();
  // Run E2: chat removed (no "messages" notification setting any more).
  const [notificationSettings, setNotificationSettings] = useState({
    assignments: true,
  });
  const [loadingSettings, setLoadingSettings] = useState(false);

  // Update notification settings when profile is loaded
  useEffect(() => {
    if (profile) {
      setNotificationSettings({
        assignments: profile.notifications?.assignments ?? true,
      });
    }
  }, [profile]);

  const saveNotificationSettings = async () => {
    setLoadingSettings(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          notifications: {
            assignments: notificationSettings.assignments,
          },
        })
        .eq("id", user.id)
        .select("*");

      if (error) {
        toast.error(t.saveError);
        return;
      }

      // Refresh the profile to get updated data
      await fetchUserProfile(user);

      toast.success(t.saveOk);
      onClose();
    } catch (error) {
      toast.error(t.saveError);
    } finally {
      setLoadingSettings(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal active">
      <div className="modal-content">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {t.title}
            </h2>
            <button
              onClick={onClose}
              aria-label={t.close}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t.choose}
              </h3>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-6 w-6 text-green-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        {t.assignments}
                      </p>
                      <p className="text-sm text-gray-500">
                        {t.assignmentsHelp}
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={notificationSettings.assignments}
                      onChange={(e) =>
                        setNotificationSettings((prev) => ({
                          ...prev,
                          assignments: e.target.checked,
                        }))
                      }
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {t.cancel}
              </button>
              <button
                onClick={saveNotificationSettings}
                disabled={loadingSettings}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingSettings ? (
                  <div className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {t.saving}
                  </div>
                ) : (
                  t.save
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentSettingsModal;
